import { useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowRight, CheckCircle2, Circle, Copy, ExternalLink, FolderKanban, History, Lock } from 'lucide-react'
import { apiGet } from '@/api/axios-client'
import { qk } from '@/api/query-keys'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ErrorState, LoadingState } from '@/components/composites/states'
import { cn } from '@/lib/utils'
import { paths } from '@/routes/paths'
import type { Client } from '@/types/entities'
import { pipelineApi, usePipelineBoard, usePipelineDeal, usePipelineWrite, type DealDetail, type DealStatus, type PilotFacts, type Stage } from './api'
import { dateInput, daysAgo, Dot, LEVEL_LABEL, LEVEL_SOFT, money, NotInGhl, shortDay, STAGE_LABEL, STATUS_LABEL, STATUS_STYLE } from './shared'

/** C4 — one deal: what it needs next, the pilot facts, discovery, its project, and what happened. */
export function DealSheet({ id, onClose, onOpen }: { id: string | null; onClose: () => void; onOpen: (id: string) => void }) {
  const query = usePipelineDeal(id)
  return (
    <Sheet open={Boolean(id)} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:w-[44rem] sm:max-w-none">
        {query.isPending ? (
          <LoadingState label="Loading the deal" />
        ) : query.error ? (
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        ) : (
          <DealBody d={query.data} onOpen={onOpen} />
        )}
      </SheetContent>
    </Sheet>
  )
}

function DealBody({ d, onOpen }: { d: DealDetail; onOpen: (id: string) => void }) {
  const { workspaceSlug = '' } = useParams()
  const can = usePipelineBoard().data?.can ?? { edit: false, seeValue: false, openProject: false, configure: false }
  const deal = d.deal
  const id = deal.id
  return (
    <div className="grid gap-5 pb-8">
      <SheetHeader className="space-y-1 text-left">
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="rounded-full bg-accent px-2 py-0.5 font-medium text-foreground">{STAGE_LABEL[deal.stage]}</span>
          {!deal.inGhl ? <NotInGhl /> : <span>GHL: {deal.pipeline} · {deal.ghlStage}</span>}
          <span>· {deal.daysInStage} days in stage</span>
        </div>
        <SheetTitle className="text-xl">{deal.company.name}</SheetTitle>
        <SheetDescription>
          {deal.name !== deal.company.name ? `${deal.name} · ` : ''}
          {deal.owner.name ?? 'No owner'}
          {deal.owner.name && !deal.owner.memberId ? ' (not matched to a Tapwork member)' : ''}
          {deal.primaryContact ? ` · ${deal.primaryContact.name}` : ''}
        </SheetDescription>
        <div className="flex flex-wrap items-center gap-3 pt-1 text-sm">
          <a href={d.ghlUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">Open in GoHighLevel<ExternalLink className="size-3.5" /></a>
          {deal.company.accountKey ? <Link to={paths.crmAccount(workspaceSlug, deal.company.accountKey)} className="text-brand hover:underline">Account page</Link> : null}
          <span className="text-muted-foreground">Last contact {daysAgo(deal.lastContactAt)}</span>
          {deal.nextMeeting ? <span className="text-muted-foreground">· Next meeting {shortDay(deal.nextMeeting.startAt)}</span> : null}
        </div>
      </SheetHeader>

      <div className={cn('flex items-start gap-2 rounded-lg px-3 py-2 text-sm', LEVEL_SOFT[deal.level])}>
        <Dot level={deal.level} className="mt-1.5" />
        <div>
          <div className="font-medium">{LEVEL_LABEL[deal.level]}</div>
          {deal.reasons.length ? <div className="text-xs">{deal.reasons.join(' · ')}</div> : <div className="text-xs">Next step set, nothing late.</div>}
        </div>
      </div>

      {d.duplicates.length ? (
        <div className="rounded-lg border border-warning bg-warning-soft px-3 py-2 text-sm text-warning">
          <div className="flex items-center gap-1.5 font-medium"><Copy className="size-4" />Another open deal for this company</div>
          <ul className="mt-1 text-xs">
            {d.duplicates.map((x) => (
              <li key={x.id}>
                <button type="button" className="underline" onClick={() => onOpen(x.id)}>{x.name}</button> — {STAGE_LABEL[x.stage]}{x.owner ? `, ${x.owner}` : ''}. Merge the two in GoHighLevel so the company has one live deal.
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <NextStep key={`${deal.nextStep}|${deal.nextStepAt}`} id={id} deal={deal} canEdit={can.edit} />
      {can.seeValue ? <Value key={String(deal.value)} id={id} value={deal.value} pending={deal.valuePending} canEdit={can.edit} /> : null}
      <Move d={d} canEdit={can.edit} />
      <Facts key={JSON.stringify(deal.facts)} id={id} facts={deal.facts} canEdit={can.edit} stage={deal.stage} />
      <Discovery id={id} items={d.discovery} canEdit={can.edit} />
      <ProjectBlock d={d} canOpen={can.openProject} />
      <Section title="History" icon={<History className="size-4" />}>
        {d.history.length ? (
          <ol className="grid gap-2">
            {d.history.map((h, i) => (
              <li key={i} className="text-sm">
                <span className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                {h.ghl === 'not_written' ? <NotInGhl className="ml-2" /> : h.ghl === 'failed' ? <span className="ml-2 text-tiny text-danger">GHL write failed</span> : null}
                <div>{h.text}</div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing recorded in Tapwork yet. Stage changes made in GoHighLevel show on the card.</p>
        )}
      </Section>
    </div>
  )
}

function Section({ title, icon, children, aside }: { title: string; icon?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <section className="rounded-xl border border-border p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">{icon}{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  )
}

function NextStep({ id, deal, canEdit }: { id: string; deal: DealDetail['deal']; canEdit: boolean }) {
  const [text, setText] = useState(deal.nextStep ?? '')
  const [at, setAt] = useState(dateInput(deal.nextStepAt))
  const save = usePipelineWrite((a: { t: string; at: string }) => pipelineApi.nextStep(id, a.t || null, a.at || null), () => 'Next step saved')
  const status = usePipelineWrite((s: DealStatus | null) => pipelineApi.status(id, s), () => 'Status saved')
  const dirty = text !== (deal.nextStep ?? '') || at !== dateInput(deal.nextStepAt)
  return (
    <Section
      title="Next step"
      aside={
        <div className="flex gap-1">
          {(['on_track', 'at_risk', 'blocked'] as const).map((s) => (
            <button key={s} type="button" disabled={!canEdit || status.isPending} aria-pressed={deal.dealStatus === s} onClick={() => status.mutate(deal.dealStatus === s ? null : s)} className={cn('rounded-full px-2 py-0.5 text-xs', deal.dealStatus === s ? STATUS_STYLE[s] + ' font-medium' : 'border border-border text-muted-foreground hover:bg-accent')}>
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      }
    >
      <div className="flex flex-wrap gap-2">
        <Input value={text} onChange={(e) => setText(e.target.value)} disabled={!canEdit} placeholder="What happens next, in one line (e.g. Send the SOW to Maria)" className="min-w-64 flex-1" maxLength={400} />
        <Input type="date" value={at} onChange={(e) => setAt(e.target.value)} disabled={!canEdit} className="w-40" aria-label="By when" />
        <Button size="sm" disabled={!canEdit || !dirty || save.isPending} onClick={() => save.mutate({ t: text.trim(), at })}>Save</Button>
      </div>
    </Section>
  )
}

function Value({ id, value, pending, canEdit }: { id: string; value: number | null; pending: boolean; canEdit: boolean }) {
  const [v, setV] = useState(value ? String(value) : '')
  const save = usePipelineWrite((n: number) => pipelineApi.value(id, n), () => 'Value saved')
  const n = Number(v.replace(/[^0-9.]/g, ''))
  return (
    <Section title="Deal value" aside={pending ? <NotInGhl /> : <span className="text-xs text-muted-foreground">Needed from Pilot agreed</span>}>
      <div className="flex gap-2">
        <Input inputMode="decimal" value={v} onChange={(e) => setV(e.target.value)} disabled={!canEdit} placeholder="Annual contract value, USD" className="w-56" />
        <Button size="sm" variant="outline" disabled={!canEdit || !(n >= 0) || n === (value ?? 0) || save.isPending} onClick={() => save.mutate(n)}>Save</Button>
        <span className="self-center text-sm text-muted-foreground">{money(value)}</span>
      </div>
    </Section>
  )
}

function Move({ d, canEdit }: { d: DealDetail; canEdit: boolean }) {
  const { workspaceSlug = '' } = useParams()
  const [lostReason, setLostReason] = useState('')
  const [askLost, setAskLost] = useState(false)
  const [needsClient, setNeedsClient] = useState(false)
  const move = usePipelineWrite(
    (a: { stage: Stage; lostReason: string | null }) => pipelineApi.move(d.deal.id, a.stage, a.lostReason),
    (r, a) => {
      if (r.project && 'needsClient' in r.project) setNeedsClient(true)
      const where = r.ghl === 'written' ? '' : r.ghl === 'failed' ? ' (GoHighLevel did not take it)' : ' (kept in Tapwork; GHL not changed)'
      return `Moved to ${STAGE_LABEL[a.stage]}${where}${r.project && 'opened' in r.project ? ' · Client Project opened' : ''}`
    },
  )
  return (
    <Section title="Move to" aside={<span className="text-xs text-muted-foreground">Greyed out: what is still missing shows on hover</span>}>
      <div className="flex flex-wrap gap-1.5">
        {d.gates.map((g) => {
          const blocked = g.missing.length > 0 && g.stage !== 'lost'
          return (
            <Button
              key={g.stage}
              size="sm"
              variant={g.stage === 'won' ? 'default' : 'outline'}
              disabled={!canEdit || move.isPending || blocked}
              title={blocked ? `Still needed: ${g.missing.join('; ')}` : undefined}
              onClick={() => (g.stage === 'lost' ? setAskLost(true) : move.mutate({ stage: g.stage, lostReason: null }))}
            >
              {blocked ? <Lock className="size-3.5" /> : <ArrowRight className="size-3.5" />}
              {g.label}
            </Button>
          )
        })}
      </div>
      {d.gates.some((g) => g.missing.length && g.stage !== 'lost') ? (
        <ul className="mt-3 grid gap-1 text-xs text-muted-foreground">
          {d.gates
            .filter((g) => g.missing.length && g.stage !== 'lost' && g.stage !== 'won')
            .slice(0, 3)
            .map((g) => (
              <li key={g.stage}><span className="font-medium text-foreground">{g.label}</span> needs: {g.missing.join(', ')}</li>
            ))}
        </ul>
      ) : null}
      {askLost ? (
        <div className="mt-3 flex flex-wrap gap-2">
          <Input value={lostReason} onChange={(e) => setLostReason(e.target.value)} placeholder="Why it was lost (price, timing, chose a competitor…)" className="min-w-64 flex-1" maxLength={500} />
          <Button size="sm" variant="destructive" disabled={!lostReason.trim() || move.isPending} onClick={() => move.mutate({ stage: 'lost', lostReason: lostReason.trim() })}>Mark lost</Button>
          <Button size="sm" variant="ghost" onClick={() => setAskLost(false)}>Cancel</Button>
        </div>
      ) : null}
      {needsClient && !d.deal.project ? (
        <p className="mt-3 rounded-md bg-info-soft px-3 py-2 text-xs text-info">The pilot is agreed: pick the client below to open its Client Project. Next time this company is known and it opens by itself.</p>
      ) : null}
      {d.deal.project ? (
        <p className="mt-3 text-xs text-muted-foreground">
          Its project: <Link className="text-brand hover:underline" to={paths.clientProject(workspaceSlug, d.deal.project.id)}>{d.deal.project.name}</Link>
        </p>
      ) : null}
    </Section>
  )
}

const FACT_FIELDS: Array<{ key: keyof PilotFacts; label: string; type: 'text' | 'date' | 'long' | 'email' }> = [
  { key: 'successCriteria', label: 'What success looks like', type: 'long' },
  { key: 'startAt', label: 'Pilot start', type: 'date' },
  { key: 'endAt', label: 'Pilot end', type: 'date' },
  { key: 'decisionAt', label: 'Decision date', type: 'date' },
  { key: 'sponsorName', label: 'Client sponsor', type: 'text' },
  { key: 'sponsorEmail', label: 'Sponsor email', type: 'email' },
]

function Facts({ id, facts, canEdit, stage }: { id: string; facts: PilotFacts; canEdit: boolean; stage: Stage }) {
  const [form, setForm] = useState<PilotFacts>(facts)
  const save = usePipelineWrite((f: Partial<PilotFacts>) => pipelineApi.facts(id, f), () => 'Pilot facts saved')
  const changed = Object.fromEntries(
    FACT_FIELDS.filter((f) => (form[f.key] ?? '') !== (facts[f.key] ?? '')).map((f) => [f.key, (form[f.key] as string | null) || null]),
  ) as Partial<PilotFacts>
  if (form.outcome !== facts.outcome) changed.outcome = form.outcome
  const done = [facts.successCriteria, facts.startAt, facts.endAt, facts.decisionAt, facts.sponsorName].filter(Boolean).length
  const important = ['pilot_agreed', 'pilot_running', 'pilot_review', 'contract'].includes(stage)
  return (
    <Section title="The pilot" aside={<span className={cn('text-xs', done === 5 ? 'text-success' : important ? 'text-warning' : 'text-muted-foreground')}>{done}/5 facts — all five before Pilot running</span>}>
      <div className="grid gap-3 sm:grid-cols-3">
        {FACT_FIELDS.map((f) => (
          <label key={f.key} className={cn('grid gap-1 text-xs text-muted-foreground', f.type === 'long' && 'sm:col-span-3')}>
            {f.label}
            {f.type === 'long' ? (
              <Textarea rows={2} value={form[f.key] ?? ''} disabled={!canEdit} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} placeholder="Agreed with the client, in their words: e.g. 3 roles filled from Tovi shortlists in 30 days" />
            ) : (
              <Input type={f.type} value={f.type === 'date' ? dateInput(form[f.key] as string | null) : (form[f.key] as string | null) ?? ''} disabled={!canEdit} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
            )}
          </label>
        ))}
        <div className="grid gap-1 text-xs text-muted-foreground">
          Review outcome
          <div className="flex gap-1">
            {([['go', 'Go'], ['no_go', 'No go'], [null, 'Not yet']] as const).map(([v, label]) => (
              <button key={label} type="button" disabled={!canEdit} aria-pressed={form.outcome === v} onClick={() => setForm({ ...form, outcome: v })} className={cn('rounded-md border px-2 py-1.5 text-xs', form.outcome === v ? 'border-brand bg-accent font-medium text-foreground' : 'border-border')}>
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" disabled={!canEdit || !Object.keys(changed).length || save.isPending} onClick={() => save.mutate(changed)}>Save the pilot facts</Button>
      </div>
    </Section>
  )
}

function Discovery({ id, items, canEdit }: { id: string; items: DealDetail['discovery']; canEdit: boolean }) {
  const toggle = usePipelineWrite((a: { key: string; done: boolean }) => pipelineApi.discovery(id, a.key, a.done))
  return (
    <Section title="Discovery, alongside the pilot" aside={<span className="text-xs text-muted-foreground">{items.filter((i) => i.done).length}/{items.length}</span>}>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {items.map((i) => (
          <li key={i.key}>
            <button type="button" disabled={!canEdit || toggle.isPending} onClick={() => toggle.mutate({ key: i.key, done: !i.done })} className="flex items-center gap-2 text-left text-sm">
              {i.done ? <CheckCircle2 className="size-4 text-success" /> : <Circle className="size-4 text-text-muted" />}
              <span className={cn(i.done && 'text-muted-foreground line-through')}>{i.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </Section>
  )
}

function ProjectBlock({ d, canOpen }: { d: DealDetail; canOpen: boolean }) {
  const { workspaceSlug = '' } = useParams()
  const p = d.deal.project
  const clients = useQuery({ queryKey: qk.clients(), queryFn: () => apiGet<{ items: Client[] }>('clients'), staleTime: 5 * 60_000, enabled: !p && canOpen })
  const [clientId, setClientId] = useState(d.rememberedClientId ?? d.clientSuggestions[0]?.id ?? '')
  const open = usePipelineWrite((cid: string) => pipelineApi.openProject(d.deal.id, cid, null), () => 'Client Project opened')
  if (p) {
    const behind = p.expectedPhase && p.expectedPhase !== p.phase
    return (
      <Section title="Client Project" icon={<FolderKanban className="size-4" />}>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Link to={paths.clientProject(workspaceSlug, p.id)} className="font-medium text-brand hover:underline">{p.name}</Link>
          <span className="rounded-full bg-accent px-2 py-0.5 text-xs">{p.phaseLabel}</span>
          {p.health ? <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', LEVEL_SOFT[p.health])}>Project {p.health === 'green' ? 'healthy' : p.health === 'yellow' ? 'at risk' : 'off track'}</span> : null}
        </div>
        {behind ? <p className="mt-2 text-xs text-warning">The deal is at {STAGE_LABEL[d.deal.stage]}, which is the {p.expectedPhase?.replace('_', ' ')} phase; the project is still in {p.phaseLabel}. Move it on from the project's dashboard.</p> : null}
      </Section>
    )
  }
  const options = [...d.clientSuggestions.map((s) => ({ id: s.id, name: `${s.name} (${s.why})` })), ...(clients.data?.items ?? []).filter((c) => !d.clientSuggestions.some((s) => s.id === c.id)).map((c) => ({ id: c.id, name: c.name }))]
  return (
    <Section title="Client Project" icon={<FolderKanban className="size-4" />} aside={<span className="text-xs text-muted-foreground">Opens in Pilot at Pilot agreed</span>}>
      {canOpen ? (
        <div className="flex flex-wrap gap-2">
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="h-9 min-w-64 flex-1 rounded-md border border-input bg-background px-2 text-sm" aria-label="Client">
            <option value="">Choose the client…</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          <Button size="sm" disabled={!clientId || open.isPending} onClick={() => open.mutate(clientId)}>Open the Client Project</Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No project yet. Someone who can open projects will see a button here.</p>
      )}
    </Section>
  )
}
